import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileText, Trash2, ChevronRight, Upload, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useDeliveryProofList, useDeleteDeliveryProof, useUploadSignedPdf, useSendDeliveryProof, deliveryProofApi } from '@/features/delivery-proof/api/deliveryProofApi';
import { DeliveryProofSendModal } from './DeliveryProofSendModal';
import { DeliveryProofState } from '@/features/delivery-proof/types/delivery-proof.types';
import type { DeliveryProofResponseDto } from '@/features/delivery-proof/types/delivery-proof.types';
import { usePermissions } from '@/hooks/auth/usePermissions';
import { DataList } from '@/Components/shared/List';
import { useListState } from '@/hooks/list';
import type { ColumnDef, RowAction, BaseListFilters } from '@/types/list.types';
import { FormInput } from '@/Components/shared/FormInput';
import { FormSelect } from '@/Components/shared/FormSelect';
import { useConfirm } from '@/hooks/useConfirm';
import { useCurrentUserWording } from '@/hooks/useVendorWording';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { DeliveryProofMobileCards } from './DeliveryProofMobileCards';

// ==================== Types ====================

type TabId = 'list' | 'drafts';

interface DeliveryProofFilters extends BaseListFilters {
  searchTerm?: string;
  state?: DeliveryProofState;
}

interface DeliveryProofListPageProps {
  onCreateClick?: () => void;
  onDraftClick?: (dpId: string) => void;
  /** Embedded mode (no outer padding/margins). Default: false */
  embedded?: boolean;
  /** Filtrer par société (pour les onglets de la vue société) */
  societyId?: string;
  /** Callback pour naviguer vers un contrat (navigation inter-onglets dans la vue société) */
  onNavigateToContract?: (contractId: number) => void;
}

const STEP_LABELS_BASE: Record<number, string> = {
  1: 'Contrat',
  3: 'Éléments',
  4: 'Véhicule',
};

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffH < 24) return `Il y a ${diffH}h`;
  if (diffD === 1) return 'Hier';
  if (diffD < 7) return `Il y a ${diffD} jours`;

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// ==================== Page principale ====================

export const DeliveryProofListPage: React.FC<DeliveryProofListPageProps> = ({ onCreateClick, onDraftClick, embedded = false, societyId }) => {
  const { can } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabId) || 'list';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [listTotal, setListTotal] = useState<number | undefined>();

  const showDraftsTab = !!onDraftClick;

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'drafts') {
      next.set('tab', 'drafts');
    } else {
      next.delete('tab');
    }
    setSearchParams(next);
  };

  // Charger le count des brouillons (même quand on est sur l'onglet principal)
  const { data: draftsData } = useDeliveryProofList({
    state: DeliveryProofState.DRAFT,
    limit: 1,
    page: 1,
    excludeLegacy: true,
  });
  const draftsCount = draftsData?.total || 0;

  const pluralize = (count: number) => (count > 1 ? 'éléments' : 'élément');

  return (
    <div>
      {/* Header avec navigation inline dans le titre */}
      <div className={embedded ? 'tw-pt-4 tw-mb-4' : 'tw-container tw-mx-auto tw-px-4 tw-pt-8 tw-mb-4'}>
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3">
            <FileText className="tw-w-8 tw-h-8 tw-text-affinicar-primary" />
            <div className="tw-flex tw-items-center tw-gap-3">
              <button
                onClick={() => handleTabChange('list')}
                className={`tw-text-2xl tw-font-bold tw-transition-colors tw-pb-1 ${
                  activeTab === 'list'
                    ? 'tw-text-gray-900 tw-border-b-2 tw-border-affinicar-primary'
                    : 'tw-text-gray-500 hover:tw-text-gray-700 tw-cursor-pointer'
                }`}
              >
                Attestations de livraison
                {listTotal !== undefined && (
                  <span className="tw-ml-1 tw-text-base tw-font-normal tw-text-gray-400">
                    ({listTotal} {pluralize(listTotal)})
                  </span>
                )}
              </button>

              {showDraftsTab && draftsCount > 0 && (
                <>
                  <span className="tw-text-2xl tw-text-gray-200 tw-font-light">|</span>
                  <button
                    onClick={() => handleTabChange('drafts')}
                    className={`tw-text-2xl tw-font-bold tw-transition-colors tw-pb-1 ${
                      activeTab === 'drafts'
                        ? 'tw-text-gray-900 tw-border-b-2 tw-border-affinicar-primary'
                        : 'tw-text-gray-500 hover:tw-text-gray-700 tw-cursor-pointer'
                    }`}
                  >
                    À finaliser
                    <span className="tw-ml-1.5 tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700 tw-rounded-full tw-px-2 tw-py-0.5 tw-align-middle">
                      {draftsCount}
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>

          {onCreateClick && can('delivery_proof.create') && (
            <button
              onClick={onCreateClick}
              className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition tw-font-medium"
            >
              + Nouvelle attestation
            </button>
          )}
        </div>
      </div>

      {/* Contenu de l'onglet actif */}
      {activeTab === 'list' ? (
        <DeliveryProofsTab onTotalChange={setListTotal} societyId={societyId} />
      ) : showDraftsTab ? (
        <DraftsTab onDraftClick={onDraftClick!} />
      ) : null}
    </div>
  );
};

// ==================== Onglet Attestations ====================

function DeliveryProofsTab({ onTotalChange, societyId }: { onTotalChange: (total: number) => void; societyId?: string }) {
  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    resetFilters,
    activeFiltersCount,
  } = useListState<DeliveryProofFilters>({
    defaultLimit: 20,
    persistInUrl: true,
  });

  const { data, isLoading, error } = useDeliveryProofList({
    page: pagination.page,
    limit: pagination.limit,
    state: filters.state,
    excludeState: DeliveryProofState.DRAFT,
    search: filters.searchTerm || undefined,
    ...(societyId ? { societyId } : {}),
  });

  const deliveryProofs = useMemo(() => data?.data || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  // Remonter le total au parent pour l'affichage dans le tab
  useEffect(() => { onTotalChange(total); }, [total, onTotalChange]);

  const getStateBadgeClass = (state: DeliveryProofState) => {
    switch (state) {
      case DeliveryProofState.PENDING_SIGNATURE:
        return 'tw-bg-yellow-100 tw-text-yellow-800';
      case DeliveryProofState.PENDING_UPLOAD:
        return 'tw-bg-orange-100 tw-text-orange-800';
      case DeliveryProofState.SENT:
        return 'tw-bg-blue-100 tw-text-blue-800';
      case DeliveryProofState.SIGNED:
        return 'tw-bg-green-100 tw-text-green-800';
      case DeliveryProofState.CANCELED:
        return 'tw-bg-red-100 tw-text-red-800';
      default:
        return 'tw-bg-gray-100 tw-text-gray-800';
    }
  };

  const getStateLabel = (state: DeliveryProofState) => {
    switch (state) {
      case DeliveryProofState.PENDING_SIGNATURE:
        return 'En attente signature';
      case DeliveryProofState.PENDING_UPLOAD:
        return 'En attente upload';
      case DeliveryProofState.SENT:
        return 'Envoyée';
      case DeliveryProofState.SIGNED:
        return 'Signée';
      case DeliveryProofState.CANCELED:
        return 'Annulée';
      default:
        return state;
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const columns: ColumnDef<DeliveryProofResponseDto>[] = [
    {
      key: 'societyName',
      label: 'Société',
      render: (dp) => (
        <div>
          <div className="tw-text-sm tw-font-medium">{dp.societyName || '-'}</div>
          {dp.creatorName && (
            <div className="tw-text-xs tw-text-gray-500">{dp.creatorName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'contractNum',
      label: 'N° Contrat',
      render: (dp) => (
        <div>
          <span className="tw-text-sm tw-font-medium">
            {dp.contractNum || '-'}
          </span>
          {dp.dpNumber && (
            <div className="tw-text-xs tw-text-gray-500">BC: {dp.dpNumber}</div>
          )}
        </div>
      ),
    },
    {
      key: 'beneficiary',
      label: 'Bénéficiaire',
      render: (dp) =>
        dp.beneficiary ? (
          <div>
            <div className="tw-text-sm tw-font-medium">
              {dp.beneficiary.firstname} {dp.beneficiary.lastname}
            </div>
            {dp.beneficiary.email && (
              <div className="tw-text-xs tw-text-gray-500">{dp.beneficiary.email}</div>
            )}
          </div>
        ) : (
          <span className="tw-text-sm tw-text-gray-400">-</span>
        ),
    },
    {
      key: 'vehicle',
      label: 'Véhicule',
      render: (dp) =>
        dp.vehicle ? (
          <div>
            <div className="tw-text-sm tw-font-medium">{dp.vehicle.registration}</div>
            <div className="tw-text-xs tw-text-gray-500">
              {dp.vehicle.brand} {dp.vehicle.model}
            </div>
          </div>
        ) : (
          <span className="tw-text-sm tw-text-gray-400">-</span>
        ),
    },
    {
      key: 'createdAt',
      label: 'Date création',
      render: (dp) => formatDate(dp.createdAt),
    },
    {
      key: 'state',
      label: 'État',
      render: (dp) => (
        <span
          className={`tw-px-2 tw-py-1 tw-inline-flex tw-text-xs tw-leading-5 tw-font-semibold tw-rounded-full ${getStateBadgeClass(dp.state)}`}
        >
          {getStateLabel(dp.state)}
        </span>
      ),
    },
  ];

  const uploadMutation = useUploadSignedPdf();
  const sendMutation = useSendDeliveryProof();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetId, setUploadTargetId] = useState<string | null>(null);
  const [resendDp, setResendDp] = useState<DeliveryProofResponseDto | null>(null);
  const [isResending, setIsResending] = useState(false);

  const handleUploadClick = (dpId: string) => {
    setUploadTargetId(dpId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && uploadTargetId) {
      uploadMutation.mutate({ id: uploadTargetId, file });
    }
    // Reset pour permettre de re-sélectionner le même fichier
    e.target.value = '';
    setUploadTargetId(null);
  };

  const rowActions: RowAction<DeliveryProofResponseDto>[] = [
    {
      label: 'Uploader le PDF signé',
      icon: Upload,
      onClick: (dp) => handleUploadClick(dp.id),
      visible: (dp) => dp.state === DeliveryProofState.PENDING_UPLOAD,
      variant: 'primary',
      permission: 'delivery_proof.create',
    },
    {
      label: 'Renvoyer le lien de signature',
      icon: Send,
      onClick: (dp) => setResendDp(dp),
      visible: (dp) => dp.state === DeliveryProofState.PENDING_SIGNATURE,
      variant: 'primary',
      permission: 'delivery_proof.create',
    },
    {
      label: 'Télécharger',
      icon: Download,
      onClick: (dp) => {
        const filename = `attestation-livraison-${dp.contractNum || dp.dpNumber || dp.id}.pdf`;
        deliveryProofApi.downloadPdf(dp.id, filename);
      },
      visible: (dp) =>
        dp.state === DeliveryProofState.PENDING_SIGNATURE ||
        dp.state === DeliveryProofState.PENDING_UPLOAD ||
        dp.state === DeliveryProofState.SENT ||
        dp.state === DeliveryProofState.SIGNED,
      variant: 'primary',
      permission: 'delivery_proof.create',
    },
  ];

  return (
    <>
    <input
      ref={fileInputRef}
      type="file"
      accept="application/pdf"
      className="tw-hidden"
      onChange={handleFileChange}
    />
    <DataList
      embedded
      data={deliveryProofs}
      total={total}
      isLoading={isLoading}
      error={error as Error}
      columns={columns}
      filterSearch={
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
          <FormInput
            type="text"
            value={filters.searchTerm || ''}
            onChange={(value) => updateFilter('searchTerm', value)}
            placeholder="Rechercher..."
            size="sm"
          />
        </div>
      }
      filters={
        <>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">État</span>
            <FormSelect
              value={filters.state || ''}
              onChange={(value) =>
                updateFilter('state', value ? (value as DeliveryProofState) : undefined)
              }
              options={[
                { value: '', label: 'Tous les états' },
                { value: DeliveryProofState.PENDING_SIGNATURE, label: 'En attente signature' },
                { value: DeliveryProofState.PENDING_UPLOAD, label: 'En attente upload' },
                { value: DeliveryProofState.SENT, label: 'Envoyée' },
                { value: DeliveryProofState.SIGNED, label: 'Signée' },
                { value: DeliveryProofState.CANCELED, label: 'Annulée' },
              ]}
              size="sm"
            />
          </div>
        </>
      }
      activeFiltersCount={activeFiltersCount}
      onResetFilters={resetFilters}
      pagination={pagination}
      onPaginationChange={setPagination}
      rowActions={rowActions}
      getRowKey={(dp) => dp.id}
      emptyMessage="Aucune attestation trouvée"
      mobileCardView={DeliveryProofMobileCards}
    />
    <DeliveryProofSendModal
      isOpen={!!resendDp}
      onClose={() => setResendDp(null)}
      onConfirmSend={(email, phone) => {
        if (!resendDp) return;
        setIsResending(true);
        sendMutation.mutate(
          { id: resendDp.id, data: { signatureType: 1, email, phone } },
          {
            onSuccess: () => {
              setIsResending(false);
              setResendDp(null);
              toast.success('Lien de signature renvoyé avec succès');
            },
            onError: (error: any) => {
              setIsResending(false);
              toast.error(error?.response?.data?.message || 'Erreur lors de l\'envoi du lien de signature');
            },
          },
        );
      }}
      isSending={isResending}
      initialEmail={resendDp?.beneficiary?.email || ''}
      initialPhone={resendDp?.beneficiary?.phone || ''}
    />
    </>
  );
}

// ==================== Onglet Brouillons ====================

function DraftsTab({ onDraftClick }: { onDraftClick: (dpId: string) => void }) {
  const wording = useCurrentUserWording();
  const STEP_LABELS: Record<number, string> = useMemo(() => ({
    ...STEP_LABELS_BASE,
    2: wording.plural,
  }), [wording]);

  const { currentUser } = useAuthStore();
  const isInternal = currentUser?.type === 'INTERNAL';

  const deleteMutation = useDeleteDeliveryProof();
  const { confirm, ConfirmDialogComponent } = useConfirm();

  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
  } = useListState<DeliveryProofFilters>({
    defaultLimit: 20,
  });

  const { data, isLoading } = useDeliveryProofList({
    state: DeliveryProofState.DRAFT,
    page: pagination.page,
    limit: pagination.limit,
    search: filters.searchTerm || undefined,
    excludeLegacy: true,
  });

  const drafts = useMemo(() => data?.data || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  const columns: ColumnDef<DeliveryProofResponseDto>[] = useMemo(
    () => [
      ...(isInternal ? [{
        key: 'society',
        label: 'Société',
        render: (dp: DeliveryProofResponseDto) => (
          <div>
            <div className="tw-text-sm tw-font-medium">{dp.societyName || '-'}</div>
            {dp.creatorName && (
              <div className="tw-text-xs tw-text-gray-500">{dp.creatorName}</div>
            )}
          </div>
        ),
      } as ColumnDef<DeliveryProofResponseDto>] : []),
      {
        key: 'contractNum',
        label: 'Contrat',
        render: (dp) => (
          <div>
            {dp.contractNum ? (
              <div className="tw-text-sm tw-font-medium">{dp.contractNum}</div>
            ) : dp.draftUuid ? (
              <div className="tw-text-xs tw-text-amber-600 tw-font-medium">Brouillon contrat</div>
            ) : (
              <span className="tw-text-sm tw-text-gray-400">-</span>
            )}
            {dp.dpNumber && (
              <div className="tw-text-xs tw-text-gray-500">BC: {dp.dpNumber}</div>
            )}
          </div>
        ),
      },
      {
        key: 'beneficiary',
        label: 'Bénéficiaire',
        render: (dp) => {
          const b = dp.beneficiary;
          return (
            <div>
              <div className="tw-text-sm tw-font-medium">
                {b?.lastname ? `${b.firstname} ${b.lastname}` : '-'}
              </div>
              {b?.email && (
                <div className="tw-text-xs tw-text-gray-500">{b.email}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'vehicle',
        label: 'Véhicule',
        render: (dp) => {
          const v = dp.vehicle;
          if (!v) return <span className="tw-text-sm tw-text-gray-400">-</span>;

          const vehicleInfo = [v.brand, v.model].filter(Boolean).join(' ');
          return (
            <div>
              <div className="tw-text-sm tw-font-medium">{v.registration || '-'}</div>
              {vehicleInfo && (
                <div className="tw-text-xs tw-text-gray-500">{vehicleInfo}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'currentStep',
        label: 'Étape',
        render: (dp) => {
          const step = dp.currentStep || 1;
          return (
            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-gap-0.5">
                {[1, 2, 3, 4].map((s) => (
                  <div
                    key={s}
                    className={`tw-w-5 tw-h-1.5 tw-rounded-full ${
                      s <= step ? 'tw-bg-affinicar-primary' : 'tw-bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="tw-text-xs tw-text-gray-500">
                {step}/4 — {STEP_LABELS[step] || '?'}
              </span>
            </div>
          );
        },
      },
      {
        key: 'updatedAt',
        label: 'Dernière modification',
        render: (dp) => (
          <span className="tw-text-sm tw-text-gray-500">
            {formatRelativeDate(dp.updatedAt)}
          </span>
        ),
      },
    ],
    [isInternal, STEP_LABELS],
  );

  return (
    <>
      {ConfirmDialogComponent}
      <DataList
        embedded
        data={drafts}
        total={total}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-end">
            <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full md:tw-w-64">
              <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
              <FormInput
                type="text"
                value={filters.searchTerm || ''}
                onChange={(value) => updateFilter('searchTerm', value)}
                placeholder="Rechercher..."
                size="sm"
              />
            </div>
          </div>
        }
        pagination={pagination}
        onPaginationChange={setPagination}
        rowActions={[
          {
            label: 'Supprimer',
            icon: Trash2,
            onClick: async (dp) => {
              const confirmed = await confirm({
                title: 'Supprimer le brouillon',
                message:
                  'Êtes-vous sûr de vouloir supprimer ce brouillon ? Cette action est irréversible.',
                confirmLabel: 'Supprimer',
                cancelLabel: 'Annuler',
                variant: 'danger',
              });
              if (confirmed) deleteMutation.mutate(dp.id);
            },
            variant: 'danger',
            permission: 'delivery_proof.create',
          },
          {
            label: 'Reprendre',
            icon: ChevronRight,
            onClick: (dp) => onDraftClick(dp.id),
            variant: 'secondary',
            permission: 'delivery_proof.create',
          },
        ]}
        onRowClick={(dp) => onDraftClick(dp.id)}
        getRowKey={(dp) => dp.id}
        emptyMessage="Aucun brouillon en cours"
        mobileCardView={DeliveryProofMobileCards}
      />
    </>
  );
}
